<template>
  <!-- Placement settings -->
  <v-row v-if="selectedPlacement">
    <v-col cols="12">
      <!-- Placement Header: row 1 (Back only) -->
      <v-btn
        variant="text"
        color="secondary"
        size="small"
        density="comfortable"
        prepend-icon="mdi-keyboard-backspace"
        class="justify-start"
        :disabled="isSaving"
        data-testid="back-btn"
        @click="handleBack"
      >
        <span class="text-body-2 text-medium-emphasis">Back to Placements</span>
      </v-btn>

      <!-- Placement Header: row 2 (Name left, actions right) -->
      <div
        class="d-flex flex-column flex-sm-row justify-sm-space-between flex-wrap w-100 pt-2"
      >
        <h2 class="placement__name d-flex align-center">
          <span class="text-h4 text-sm-h4 font-weight-medium">
            {{ selectedPlacement.friendlyName }}
          </span>
        </h2>

        <!-- Placement Header: row 3 (Actions right) -->
        <div class="d-flex align-center mt-2 mt-sm-0">
          <!-- Preview Button -->
          <v-tooltip text="Open this placement in a new tab">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                data-testid="preview-btn"
                class="mb-2 mr-2"
                size="small"
                color="success"
                @click="showPreview"
              >
                <v-icon>mdi-eye</v-icon>
                <span class="d-none d-sm-inline">Preview</span>
              </v-btn>
            </template>
          </v-tooltip>

          <!-- More Actions Menu -->
          <v-menu data-testid="more-actions-menu" location="bottom end">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="mb-2"
                variant="text"
                size="small"
                color="grey-darken-1"
                min-width="60"
                data-testid="more-actions-menu-trigger"
                aria-label="More actions"
              >
                <v-icon size="small" class="mr-1">mdi-dots-vertical</v-icon>
                <span class="text-caption text-medium-emphasis">Actions</span>
              </v-btn>
            </template>
            <v-list density="compact" class="pa-2">
              <!-- Enabled Toggle -->
              <v-list-item class="mb-1" density="compact">
                <template #prepend>
                  <v-switch
                    v-model="placementEnabled"
                    density="compact"
                    hide-details
                    color="success"
                    :disabled="isSaving"
                    data-testid="placement-enabled-toggle"
                    class="ma-0 mr-3"
                  >
                  </v-switch>
                </template>
                <v-list-item-title
                  class="text-body-2 text-medium-emphasis"
                  data-testid="placement-enabled-label"
                >
                  {{ placementEnabled ? 'Enabled' : 'Disabled' }}
                </v-list-item-title>
              </v-list-item>

              <v-divider class="my-2"></v-divider>

              <!-- Copy Embed Code -->
              <v-list-item
                @click="copyToClipboard"
                class="mb-1"
                density="compact"
              >
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2"
                    >mdi-content-copy</v-icon
                  >
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">
                  {{ copyStatus ? copyStatus : 'Copy embed code' }}
                </v-list-item-title>
              </v-list-item>

              <!-- Validate Schema -->
              <v-list-item
                @click="showValidateSchemaDialog = true"
                class="mb-1"
                density="compact"
              >
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2"
                    >mdi-google</v-icon
                  >
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">
                  Validate Schema
                </v-list-item-title>
              </v-list-item>

              <v-divider class="my-2"></v-divider>

              <!-- Edit Placement Details -->
              <v-list-item
                @click="openEditDetailsDialog"
                class="mb-1"
                density="compact"
              >
                <template #prepend>
                  <v-icon size="small" color="primary" class="mr-2"
                    >mdi-pencil</v-icon
                  >
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">
                  Edit Placement Details
                </v-list-item-title>
              </v-list-item>

              <!-- Delete Placement -->
              <v-list-item
                @click="showDeleteDialog = true"
                class="mb-1"
                density="compact"
                color="error"
              >
                <template v-slot:prepend>
                  <v-icon size="small" color="error" class="mr-2"
                    >mdi-delete</v-icon
                  >
                </template>
                <v-list-item-title class="text-body-2 text-error">
                  Delete Placement
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-divider class="my-4" />
    </v-col>

    <v-col cols="12" v-if="!placementEnabled">
      <!-- Placement Status Banner -->
      <div class="mb-4">
        <v-alert
          type="warning"
          variant="tonal"
          class="mb-0"
          data-testid="placement-disabled-alert"
        >
          <template #prepend>
            <v-icon>mdi-pause-circle</v-icon>
          </template>
          <div class="d-flex align-center justify-space-between">
            <span class="text-body-1 font-weight-medium">
              This placement is currently disabled and will not display to
              users.
            </span>
            <v-btn
              color="success"
              size="small"
              variant="flat"
              @click="placementEnabled = true"
              :disabled="isSaving"
              data-testid="enable-placement-btn"
            >
              <v-icon size="small" class="mr-1">mdi-play</v-icon>
              Enable Placement
            </v-btn>
          </div>
        </v-alert>
      </div>
    </v-col>

    <v-row class="w-100">
      <!-- Placement Settings -->
      <v-col cols="12">
        <v-card class="placement-settings-card">
          <div class="d-flex justify-space-between align-center pa-6 pb-4">
            <v-tabs
              v-model="activeTab"
              color="primary"
              align-tabs="start"
              class="flex-grow-1 main-tabs"
              density="comfortable"
            >
              <v-tab value="filters" @click.prevent>
                <v-icon center class="mr-2">mdi-filter</v-icon>
                <span class="d-none d-sm-inline text-body-1 font-weight-medium"
                  >Filters</span
                >
              </v-tab>
              <v-tab value="vehicleInfo" @click.prevent>
                <v-icon center class="mr-2">mdi-car</v-icon>
                <span class="d-none d-sm-inline text-body-1 font-weight-medium"
                  >Vehicle Info</span
                >
              </v-tab>
              <v-tab value="button" @click.prevent>
                <v-icon center class="mr-2">mdi-button-cursor</v-icon>
                <span class="d-none d-sm-inline text-body-1 font-weight-medium"
                  >CTA</span
                >
              </v-tab>
              <v-tab value="display" @click.prevent>
                <v-icon center class="mr-2">mdi-cog</v-icon>
                <span class="d-none d-sm-inline text-body-1 font-weight-medium"
                  >Display</span
                >
              </v-tab>
            </v-tabs>

            <!-- Save Button -->
            <v-btn
              color="primary"
              @click="savePlacementSettings"
              :disabled="!hasChanges || isSaving"
              size="large"
              variant="elevated"
              class="ml-4"
            >
              <v-icon>mdi-content-save</v-icon>
              <span class="d-none d-sm-block ml-2">Save</span>
            </v-btn>
          </div>

          <v-window v-model="activeTab">
            <!-- Filters Tab -->
            <v-window-item value="filters">
              <v-card-text>
                <v-card>
                  <v-card-title>Filter Settings</v-card-title>
                  <v-card-text>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                      Configure vehicle filters and sorting options for your
                      inventory display
                    </p>
                    <div class="filters-section">
                      <div class="d-none justify-end">
                        <v-switch
                          v-if="!accountSettings.isClientUser"
                          class="filters__manual d-inline-block pa-0 ma-0 mr-4"
                          v-model="manualSelectionToggle"
                          density="compact"
                          hide-details
                          color="primary"
                          @update:model-value="handleSelectionModeChange"
                        >
                          <template v-slot:label>
                            <span class="text-subtitle-2"
                              >use manual selection</span
                            >
                          </template>
                        </v-switch>
                      </div>
                      <JazelInventoryInterface
                        :accountIds="accountSettings?.inventoryIds"
                        :juid="accountSettings.accountId"
                        product="micro_srp"
                        :service-endpoint="inventoryServiceEndpoint"
                        :model-value="searchResults"
                        :available-filter-groups="supportedFilterGroups"
                        :show-filters-ui="true"
                        :show-items-ui="true"
                        :selectable="allowVehicleSelection"
                        :selected-items="selectedPlacement.activeVehicles"
                        :show-search-ui="false"
                        :filters="{
                          base: {},
                          current: selectedPlacement?.activeFilters ?? {},
                        }"
                        :excluded-filters="
                          selectedPlacement?.excludedFilters ?? {}
                        "
                        :sort="{
                          field: selectedPlacement?.sortBy || 'display_price',
                          direction: selectedPlacement?.sortDirection ?? 'asc',
                          secondary: selectedPlacement?.secondarySorts || [],
                        }"
                        @update:selected="handleSelectedItemsUpdate"
                        @update:modelValue="handleSearchResults"
                        @update:filters="handleFilterChanges"
                        @update:excludedFilters="handleExcludedFilterChanges"
                        @update:sort="handleSortChange"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-window-item>

            <!-- Display Tab -->
            <v-window-item value="display">
              <v-card-text>
                <v-card>
                  <v-card-title>Display Settings</v-card-title>
                  <v-card-text>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                      Configure the layout and appearance of your vehicle cards
                    </p>
                    <v-form>
                      <v-select
                        v-model="selectedPlacement.layout.type"
                        :items="['grid', 'slider']"
                        label="Layout"
                        dense
                        hide-details
                        outlined
                        class="mb-4"
                      />
                      <v-text-field
                        label="Max number of Cards to Show"
                        v-model.number="selectedPlacement.numberOfVehicles"
                        type="number"
                        min="1"
                        class="mb-4"
                        density="comfortable"
                        hide-details
                        variant="outlined"
                      ></v-text-field>
                      <UnitSettings
                        label="Card Width"
                        :modelValue="{
                          value: selectedPlacement.cardSettings.width,
                          unit: selectedPlacement.cardSettings.widthUnit,
                        }"
                        @update:modelValue="updateCardSettings('width', $event)"
                      />
                      <UnitSettings
                        label="Card Height"
                        :modelValue="{
                          value: selectedPlacement.cardSettings.height,
                          unit: selectedPlacement.cardSettings.heightUnit,
                        }"
                        @update:modelValue="
                          updateCardSettings('height', $event)
                        "
                      />
                      <UnitSettings
                        label="Card Image Height"
                        :modelValue="{
                          value: selectedPlacement.cardSettings.imageHeight,
                          unit: selectedPlacement.cardSettings.imageHeightUnit,
                        }"
                        @update:modelValue="
                          updateCardSettings('imageHeight', $event)
                        "
                      />
                      <UnitSettings
                        label="Card Gap"
                        :modelValue="{
                          value: selectedPlacement.cardSettings.margin,
                          unit: selectedPlacement.cardSettings.marginUnit,
                        }"
                        @update:modelValue="
                          updateCardSettings('margin', $event)
                        "
                      />
                      <UnitSettings
                        label="Card Border Radius"
                        :modelValue="{
                          value: selectedPlacement.cardSettings.borderRadius,
                          unit: selectedPlacement.cardSettings.borderRadiusUnit,
                        }"
                        @update:modelValue="
                          updateCardSettings('borderRadius', $event)
                        "
                      />
                      <v-card
                        class="mt-4 pa-2"
                        v-if="selectedPlacement.layout.type === 'slider'"
                      >
                        <v-card-title class="mb-2"
                          >Slider Arrow Settings</v-card-title
                        >
                        <v-card-text>
                          <v-row>
                            <v-tabs
                              v-model="activeSliderTab"
                              density="compact"
                              center-active
                              show-arrows
                            >
                              <v-tab value="size">Size</v-tab>
                              <v-tab value="colors">Colors</v-tab>
                            </v-tabs>
                          </v-row>
                          <v-row>
                            <v-col cols="12">
                              <v-window v-model="activeSliderTab" class="pt-4">
                                <!-- Size -->
                                <v-window-item value="size">
                                  <UnitSettings
                                    label="Font Size"
                                    :modelValue="{
                                      value:
                                        selectedPlacement.sliderArrowSettings
                                          .fontSize,
                                      unit: selectedPlacement
                                        .sliderArrowSettings.fontSizeUnit,
                                    }"
                                    @update:modelValue="
                                      updateSliderArrowSettings(
                                        'fontSize',
                                        $event,
                                      )
                                    "
                                  />
                                </v-window-item>
                                <!-- Colors -->
                                <v-window-item value="colors">
                                  <ColorSettings
                                    :model-value="
                                      selectedPlacement.sliderArrowSettings
                                        .colors
                                    "
                                    :excluded="[
                                      'background',
                                      'backgroundHover',
                                    ]"
                                    @update:model-value="
                                      updateSliderArrowSettings(
                                        'colors',
                                        $event,
                                      )
                                    "
                                  />
                                </v-window-item>
                              </v-window>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-form>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-window-item>

            <!-- Vehicle Info Tab -->
            <v-window-item value="vehicleInfo">
              <v-card-text>
                <v-card>
                  <v-card-title>Vehicle Information Layout</v-card-title>
                  <v-card-text>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                      Configure how vehicle information appears on your cards
                    </p>
                    <VehicleDetailRows
                      :model-value="selectedPlacement.vehicleDetailRows"
                      @update:model-value="handleVehicleDetailRowsUpdate"
                    />
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-window-item>

            <!-- Button Tab -->
            <v-window-item value="button">
              <v-card-text>
                <v-card>
                  <v-card-title>Call to Action Settings</v-card-title>
                  <v-card-text>
                    <p class="text-body-2 text-medium-emphasis mb-4">
                      Configure the call-to-action button appearance and
                      behavior
                    </p>
                    <CTASettings v-model="selectedPlacement.callToAction" />
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-window-item>
          </v-window>
        </v-card>
      </v-col>
    </v-row>
  </v-row>

  <!-- Delete Placement Dialog -->
  <v-dialog v-model="showDeleteDialog" max-width="500px">
    <v-card>
      <v-card-title>Delete Placement</v-card-title>
      <v-card-text>
        Are you sure you want to delete the placement "{{
          selectedPlacementId
        }}"? This action cannot be undone.
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="showDeleteDialog = false">
          Cancel
        </v-btn>
        <v-btn color="error" @click="removePlacement">Delete Placement</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Confirm Discard Dialog -->
  <v-dialog v-model="showConfirmDialog" max-width="500px">
    <v-card>
      <v-card-title>Unsaved Changes</v-card-title>
      <v-card-text>
        You have unsaved changes. Are you sure you want to discard them?
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="text"
          @click="showConfirmDialog = false"
        >
          Keep Editing
        </v-btn>
        <v-btn color="error" @click="confirmDiscard"> Discard Changes </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Confirm Manual Selection Dialog -->
  <v-dialog v-model="showManualSelectionConfirmDialog" max-width="500px">
    <v-card>
      <v-card-title
        >Manual Selection
        <small class="d-block d-sm-inline-block text-red font-italic"
          >(Not recommended)</small
        >
      </v-card-title>
      <v-card-text class="text-subtitle-2">
        <p class="mb-2">
          Enabling manual selection may lead to unexpected behavior.
          <span class="text-red"
            >Selected vehicles which are taken out of inventory will not show on
            your website and a replacement should be manually selected.</span
          >
        </p>
        <p>
          It is
          <span class="text-primary">recommended to use the filtering</span>
          selection method.
        </p>
        <p>
          If you still want to proceed, please click "I understand the risks".
        </p>
      </v-card-text>
      <v-card-actions class="d-flex flex-column-reverse flex-sm-row">
        <v-spacer></v-spacer>
        <v-btn color="error" variant="text" @click="enableManualSelection()">
          I understand the risks
        </v-btn>
        <v-btn color="primary" @click="cancelSelectionModeChange"
          >Keep using filters</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Edit Placement Details Dialog -->
  <v-dialog v-model="showEditDetailsDialog" max-width="600px">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-pencil</v-icon>
        Edit Placement Details
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="savePlacementDetails">
          <v-text-field
            v-model="editPlacementName"
            label="Placement Name"
            placeholder="Enter placement name"
            variant="outlined"
            density="comfortable"
            class="mb-4"
            hide-details="auto"
            :rules="[requiredRule]"
            validate-on="input"
            required
          />
          <v-text-field
            v-model="editPlacementUrl"
            label="Placement URL"
            placeholder="https://example.com/vehicles"
            variant="outlined"
            density="comfortable"
            class="mb-4"
            hide-details="auto"
            :rules="[requiredRule]"
            validate-on="input"
            required
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey-darken-1"
          variant="text"
          @click="showEditDetailsDialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="savePlacementDetails"
          :disabled="isSavePlacementDisabled"
          :loading="isSaving"
        >
          Save & Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Validate Schema Dialog -->
  <ValidateSchemaDialog
    v-model="showValidateSchemaDialog"
    :content="validationUrl"
  />

  <!-- Snackbar -->
  <v-snackbar v-model="showSnackbar" :timeout="2000" :color="snackbarColor">
    {{ snackbarText }}

    <template #actions>
      <v-btn icon="mdi-close" @click="showSnackbar = false"></v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  CTASettings,
  JazelInventoryInterface,
  UnitSettings,
  ColorSettings,
  type IControlUnitValues,
} from '@jazel/jazel-vuetify'
import type { IVehicleFilters } from '@/types'
import VehicleDetailRows from './VehicleDetailRows.vue'
import ValidateSchemaDialog from '@/components/ValidateSchemaDialog.vue'
import type { ISettingsGroup } from '@/types'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { config } from '@/config/generated-config'

const props = defineProps<{
  accountName: string
  cdn: string
  placements: ISettingsGroup
  placementId: string | null
}>()

const emit = defineEmits<{
  'update:placementId': [value: string | null]
}>()

// Handle vehicle detail rows updates
const handleVehicleDetailRowsUpdate = (newVehicleDetailRows: any[]) => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  // Update the local state
  selectedPlacement.value.vehicleDetailRows = newVehicleDetailRows

  // Update the store
  const updatedSettings = { ...placements.value }
  updatedSettings[selectedPlacementId.value] = {
    ...updatedSettings[selectedPlacementId.value],
    vehicleDetailRows: newVehicleDetailRows,
  }

  productSettings.updateSettings(updatedSettings)
}

const searchResults = ref<any>(null)

const showValidateSchemaDialog = ref(false)
const showEditDetailsDialog = ref(false)
const editPlacementName = ref('')
const editPlacementUrl = ref('')
const requiredRule = (v: string) =>
  !!(v ?? '').trim() || 'This field is required'
const isSavePlacementDisabled = computed(() => {
  const name = (editPlacementName.value ?? '').trim()
  const url = (editPlacementUrl.value ?? '').trim()
  if (!name || !url) return true

  const currentName = (selectedPlacement.value?.friendlyName ?? '').trim()
  const currentUrl = (selectedPlacement.value?.url ?? '').trim()

  // Disable when unchanged
  return name === currentName && url === currentUrl
})
const cxUrl = config.VITE_CX_URL
const validationUrl = `${cxUrl}?account_id=${props.accountName}&placement_id=${props.placementId}`

const activeTab = ref('filters')
const activeSliderTab = ref('size')
const originalPlacement = ref(null)
const placementToDelete = ref<string | null>(null)

const accountSettings = useAccountSettingsStore()
const productSettings = useProductSettingsStore()
const placements = computed(() => props.placements ?? {})
const inventoryIds = computed(() => {
  return (accountSettings?.inventoryIds ?? []).join(',')
})
const count = ref(0)
const filteredItems = ref<any[]>([])
const isSaving = ref(false)

const placementEnabled = computed({
  get: () => {
    if (!selectedPlacementId.value || !selectedPlacement.value) {
      return false
    }
    return selectedPlacement.value.enabled ?? true
  },
  set: async (newValue: boolean) => {
    if (!selectedPlacementId.value || !selectedPlacement.value) {
      return
    }

    // Update the local state immediately
    selectedPlacement.value.enabled = newValue

    // Save only the enabled state to backend (not all tab changes)
    if (
      originalPlacement.value &&
      typeof originalPlacement.value === 'object'
    ) {
      try {
        await productSettings.saveSettings(
          {
            [selectedPlacementId.value]: {
              ...(originalPlacement.value as any),
              enabled: newValue,
            },
          },
          false,
        )

        // Update originalPlacement to reflect the saved enabled state
        ;(originalPlacement.value as any).enabled = newValue

        // Show success feedback
        showSnackbar.value = true
        snackbarText.value = `Placement ${newValue ? 'enabled' : 'disabled'} successfully`
        snackbarColor.value = 'success'
      } catch (error) {
        // Revert the local state on error
        selectedPlacement.value.enabled = !newValue

        // Show error feedback
        showSnackbar.value = true
        snackbarText.value = `Failed to ${newValue ? 'enable' : 'disable'} placement`
        snackbarColor.value = 'error'
      }
    }
  },
})

// Placement selection
const selectedPlacementId = ref<string | null>(props.placementId ?? null)

// Watch for prop changes and update selectedPlacementId
watch(
  () => props.placementId,
  newPlacementId => {
    selectedPlacementId.value = newPlacementId
  },
)

// Selected placement settings
const selectedPlacement = computed(() => {
  return selectedPlacementId.value
    ? placements.value[selectedPlacementId.value]
    : null
})

const allowVehicleSelection = ref(
  (selectedPlacement.value?.activeVehicles ?? [])?.length > 0,
)
const manualSelectionToggle = ref(allowVehicleSelection.value)
const showManualSelectionConfirmDialog = ref(false)
const showConfirmDialog = ref(false)
const pendingAction = ref<'back' | 'cancel' | null>(null)

const showSnackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const inventoryServiceEndpoint = config.VITE_INVENTORY_API
const supportedFilterGroups = [
  'years',
  'make',
  'model',
  'trim',
  'conditions',
  'state',
  'vehicle-type',
  'price',
  'days-on-lot',
  'stock-number',
  'mileage',
  'discount',
]

const handleSelectionModeChange = (allowManualSelection: any) => {
  if (allowManualSelection === true) {
    showManualSelectionConfirmDialog.value = true
  } else {
    disableManualSelection()
  }
}

const cancelSelectionModeChange = () => {
  manualSelectionToggle.value = false
  showManualSelectionConfirmDialog.value = false
}

const enableManualSelection = () => {
  allowVehicleSelection.value = true
  showManualSelectionConfirmDialog.value = false
}

const disableManualSelection = () => {
  allowVehicleSelection.value = false

  if (
    selectedPlacement.value &&
    selectedPlacement.value.activeVehicles.length > 0
  ) {
    selectedPlacement.value.activeVehicles = []
  }
}

const handleBack = async () => {
  if (hasChanges.value) {
    pendingAction.value = 'back'
    showConfirmDialog.value = true
  } else {
    await removePreviewPlacements()
    showPlacements()
  }
}

const removePreviewPlacements = async () => {
  let hasPreview = false
  const updatedSettings = { ...placements.value }

  for (const [key] of Object.entries(updatedSettings)) {
    if (key.startsWith('preview__')) {
      // Remove preview placements
      delete updatedSettings[key]
      hasPreview = true
    }
  }

  if (hasPreview) {
    await productSettings.saveSettings(updatedSettings)
  }
}

const copyStatus = ref('')
const copyToClipboard = async () => {
  try {
    const content = `<script src='${props.cdn}/micro-srp/cx/loader.js'><\/script><div class='micro-srp' data-account-id='${props.accountName}' data-placement-id='${props.placementId}'></div>`
    await navigator.clipboard.writeText(content)
    copyStatus.value = 'Copied to clipboard!'

    // Reset the icon and status after 2 seconds
    setTimeout(() => {
      copyStatus.value = ''
    }, 2000)
  } catch (err) {
    copyStatus.value = 'Failed to copy text'
    console.error('Failed to copy text:', err)
  }
}

const confirmDiscard = async () => {
  // Hide the unsaved-changes dialog
  showConfirmDialog.value = false

  // Snap all fields (real + preview) back to original
  await revertPlacementChanges()

  // Go back to the list
  if (pendingAction.value === 'back') {
    // clear out the snapshot
    originalPlacement.value = null
    // show the placements list
    showPlacements()
  }

  // Reset pendingAction so future discards don’t auto-navigate
  pendingAction.value = null
}

const activeFilterGroups = computed({
  get: () => {
    if (!selectedPlacementId.value || !selectedPlacement.value) return ''
    return selectedPlacement.value.activeFilterGroups
  },
  set: (newActiveFilterGroups: string) => {
    if (!selectedPlacementId.value || !selectedPlacement.value) return

    const updatedSettings = { ...placements.value }
    updatedSettings[selectedPlacementId.value] = {
      ...updatedSettings[selectedPlacementId.value],
      activeFilterGroups: newActiveFilterGroups,
    }
    productSettings.updateSettings(updatedSettings)
  },
})

const activeFilters = computed({
  get: () => {
    if (!selectedPlacementId.value || !selectedPlacement.value) return {}
    return selectedPlacement.value.activeFilters
  },
  set: (newActiveFilters: IVehicleFilters) => {
    if (!selectedPlacementId.value || !selectedPlacement.value) return

    const updatedSettings = { ...placements.value }
    updatedSettings[selectedPlacementId.value] = {
      ...updatedSettings[selectedPlacementId.value],
      activeFilters: newActiveFilters,
    }

    productSettings.updateSettings(updatedSettings)
  },
})

// Dialog controls
const showDeleteDialog = ref(false)

// Set selected placement
const setSelectedPlacement = (placementId: string) => {
  selectedPlacementId.value = placementId

  // Create a deep copy of the original placement data
  try {
    originalPlacement.value = JSON.parse(
      JSON.stringify(placements.value[placementId] ?? {}),
    )
  } catch (err) {
    console.error('Error adding new placement', err)
  }
}

// Delete placement
const removePlacement = async () => {
  if (!selectedPlacement.value) {
    return
  }

  const placementId = selectedPlacementId.value

  if (placementId === null) {
    return
  }

  const result = await productSettings.deletePlacement(placementId)

  showSnackbar.value = true

  if (result) {
    snackbarText.value = 'Placement deleted successfully'
    snackbarColor.value = 'success'
    emit('update:placementId', null)
  } else {
    snackbarText.value = 'Failed to delete placement'
    snackbarColor.value = 'error'
    showDeleteDialog.value = false
    return
  }

  // Reset states
  placementToDelete.value = null
  showDeleteDialog.value = false
  selectedPlacementId.value = null
}

const itemsForPreview = ref<any>(selectedPlacement.value?.activeVehicles ?? [])

// Show placements
const showPlacements = () => {
  selectedPlacementId.value = null
  emit('update:placementId', null)
}

// Revert placement changes
const revertPlacementChanges = async () => {
  if (!selectedPlacementId.value || !originalPlacement.value) return

  const id = selectedPlacementId.value

  // Make a fresh copy of the snapshot
  const original = JSON.parse(JSON.stringify(originalPlacement.value))

  // Build a settings object that resets both keys
  const resetSettings = {
    ...placements.value,
    [id]: original,
    [`preview__${id}`]: original,
  }

  // Update the store
  productSettings.updateSettings(resetSettings)

  // push the preview restore to the server,
  // but avoid stomping the real placement (refreshData=false)
  await productSettings.saveSettings({ [`preview__${id}`]: original }, false)

  // Clear the undo buffer
  originalPlacement.value = null
}

// Create a stable stringify to avoid false positives due to key-order changes
const stableStringify = (value: any): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value)
  }
  if (Array.isArray(value)) {
    return '[' + value.map(stableStringify).join(',') + ']'
  }
  const keys = Object.keys(value).sort()
  return (
    '{' +
    keys
      .map(k => JSON.stringify(k) + ':' + stableStringify(value[k]))
      .join(',') +
    '}'
  )
}

const hasChanges = computed(() => {
  if (
    !selectedPlacementId.value ||
    !originalPlacement.value ||
    !selectedPlacement.value
  ) {
    return false
  }

  // Create copies and compare all fields (including friendlyName and url)
  const currentCopy = JSON.parse(JSON.stringify(selectedPlacement.value))
  const originalCopy = JSON.parse(JSON.stringify(originalPlacement.value))

  // Deep compare copies with stable key ordering to ignore insertion-order diffs
  return stableStringify(currentCopy) !== stableStringify(originalCopy)
})

const updateCardSettings = (key: string, value: IControlUnitValues) => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  const updatedSettings = { ...placements.value }
  updatedSettings[selectedPlacementId.value] = {
    ...updatedSettings[selectedPlacementId.value],
    cardSettings: {
      ...updatedSettings[selectedPlacementId.value].cardSettings,
      [key]: value.value,
      [`${key}Unit`]: value.unit,
    },
  }

  productSettings.updateSettings(updatedSettings)
}

const updateSliderArrowSettings = (key: string, value: any) => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  const updatedSettings = { ...placements.value }
  let updatedValue: any
  if (key === 'fontSize') {
    updatedValue = {
      [key]: value.value,
      [`${key}Unit`]: value.unit,
    }
  } else {
    updatedValue = {
      [key]: value,
    }
  }
  updatedSettings[selectedPlacementId.value] = {
    ...updatedSettings[selectedPlacementId.value],
    sliderArrowSettings: {
      ...updatedSettings[selectedPlacementId.value].sliderArrowSettings,
      ...updatedValue,
    },
  }

  productSettings.updateSettings(updatedSettings)
}

const handleFilterChanges = (filters: any) => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  const updatedSettings = { ...placements.value }
  updatedSettings[selectedPlacementId.value] = {
    ...updatedSettings[selectedPlacementId.value],
    activeFilters: { ...filters },
  }

  productSettings.updateSettings(updatedSettings)
}

const toSnakeCase = (v: string) =>
  v?.replace(/[A-Z]/g, m => `_${m.toLowerCase()}`) ?? ''

const handleSortChange = (sort: any) => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  const updatedSettings = { ...placements.value }
  const current = updatedSettings[selectedPlacementId.value]
  updatedSettings[selectedPlacementId.value] = {
    ...current,
    sortBy: sort.field ? toSnakeCase(sort.field) : current.sortBy,
    sortDirection: sort.direction ?? current.sortDirection,
    secondarySorts:
      (sort.secondary || []).map((s: any) => ({
        sortField: s.sortField ? toSnakeCase(s.sortField) : current.sortBy,
        sortDirection: s.sortDirection ?? current.sortDirection,
      })) || [],
  }

  productSettings.updateSettings(updatedSettings)
}

const handleExcludedFilterChanges = (excludedFilters: any) => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  const updatedSettings = { ...placements.value }
  updatedSettings[selectedPlacementId.value] = {
    ...updatedSettings[selectedPlacementId.value],
    excludedFilters: excludedFilters,
  }

  productSettings.updateSettings(updatedSettings)
}

const handleSearchResults = (searchResult: any) => {
  const noSelectedPlacement =
    !selectedPlacementId.value || !selectedPlacement.value

  if (noSelectedPlacement) {
    return
  }

  searchResults.value = searchResult
  updatePreviewItems()
}

const handleSelectedItemsUpdate = (selectedItems: any[]) => {
  const noSelectedPlacement =
    !selectedPlacementId.value || !selectedPlacement.value

  if (noSelectedPlacement) {
    return
  }

  if (selectedItems.length > 0) {
    selectedPlacement.value.activeVehicles = normalizeItems(selectedItems)
    itemsForPreview.value = selectedPlacement.value.activeVehicles
  } else {
    selectedPlacement.value.activeVehicles = []
    updatePreviewItems()
  }
}

const normalizeItems = (items: any) => {
  return items.map((item: any) => {
    const imagePath = item.visual.image.visibleUrl

    let vehicleType = item.specifications?.vehicleType ?? []
    if (Array.isArray(vehicleType)) {
      vehicleType = vehicleType.join(', ')
    }

    return {
      address: `${item.location?.address1 ?? ''} ${item.location?.address2 ?? ''}`,
      bodyType: vehicleType,
      cabStyle: item.cabStyle,
      city: item.location?.location_city ?? '',
      condition: item.conditions,
      discount: item.discount,
      drivetrain: item.specifications?.drivetrain ?? '',
      engine: item.specifications?.engine ?? '',
      exteriorColor: item.visual?.colors?.exterior?.factory ?? '',
      evBatteryRange: item.specifications?.evBatteryRange ?? '',
      evMpgCombined: item.specifications?.evMpgCombined ?? '',
      fuelType: item.specifications?.fuelType ?? '',
      image: getImageUrlFromCdn(imagePath),
      interiorColor: item.visual?.colors?.interior?.factory ?? '',
      phoneNumber: item.location?.contactNumber ?? '',
      price: item.displayPrice,
      make: item.make,
      mileage: item.mileage,
      model: item.model,
      msrp: getMsrp(item.pricing),
      pricings: item.pricing,
      state: item.location?.state ?? '',
      stockNumber: item.stockNumber,
      styleName: item.visual?.styleName ?? '',
      trim: item.trim,
      used: item.used,
      vehicleHighlights: Array.isArray(item.vehicleHighlights)
        ? item.vehicleHighlights.join(' | ')
        : item.vehicleHighlights,
      vehicleStatus: item.vehicleStatus,
      vin: item.vin,
      year: item.year,
      zipCode: item.location?.zipCode ?? '',
    }
  })
}

const getMsrp = (priceData: any) => {
  const msrpData = priceData?.find((data: any) => data.pricing.Name === '@MSRP')

  if (!msrpData) return null

  return msrpData.pricing.Text
}

const getImageUrlFromCdn = (imagePath: string) => {
  const cdn = config.VITE_IMAGE_SERVICE_API
  return `${cdn}/${imagePath}?scale=450/-1/70`
}

// Save placement settings
const savePlacementSettings = async () => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  isSaving.value = true

  try {
    // Build a payload that contains both the real placement and its preview
    const payload = {
      // real placement goes here…
      [selectedPlacementId.value]: selectedPlacement.value,
      // preview sibling goes here, ensuring secondarySorts is always an array
      [`preview__${selectedPlacementId.value}`]: {
        ...selectedPlacement.value,
        secondarySorts: selectedPlacement.value.secondarySorts ?? [],
      },
    }

    // One call to save both
    await productSettings.saveSettings(payload)

    // Refresh your “original” snapshot
    originalPlacement.value = JSON.parse(
      JSON.stringify(selectedPlacement.value),
    )

    showSnackbar.value = true
    snackbarText.value = 'Placement settings (and preview) updated successfully'
    snackbarColor.value = 'success'
  } catch (err) {
    showSnackbar.value = true
    snackbarText.value = 'Failed to save placement settings'
    snackbarColor.value = 'error'
    console.error(err)
  } finally {
    isSaving.value = false
  }
}

const showPreview = async () => {
  if (selectedPlacement.value) {
    const previewId = `preview__${selectedPlacementId.value}`

    await productSettings.saveSettings(
      {
        [previewId]: {
          ...selectedPlacement.value,
          // make sure it's always an array:
          secondarySorts: selectedPlacement.value.secondarySorts ?? [],
        },
      },
      false,
    )
  }

  window.setTimeout(() => {
    const url = `${config.VITE_CX_URL}?account_id=${accountSettings.accountId}&placement_id=preview__${selectedPlacementId.value}`
    window.open(url, '_blank')
  }, 300)
}

const openEditDetailsDialog = () => {
  if (selectedPlacement.value) {
    editPlacementName.value = selectedPlacement.value.friendlyName || ''
    editPlacementUrl.value = selectedPlacement.value.url || ''
    showEditDetailsDialog.value = true
  }
}

const savePlacementDetails = async () => {
  if (isSavePlacementDisabled.value) return
  if (selectedPlacement.value && selectedPlacementId.value) {
    // Update the local state
    selectedPlacement.value.friendlyName = editPlacementName.value
    selectedPlacement.value.url = editPlacementUrl.value

    // Close the dialog
    showEditDetailsDialog.value = false
  }
}

const updatePreviewItems = () => {
  if (!searchResults.value?.items?.length || !selectedPlacement.value) {
    return
  }

  if (selectedPlacement.value.activeVehicles.length === 0) {
    const itemsToShow = Math.max(
      selectedPlacement.value.numberOfVehicles ?? 4,
      1,
    )
    const items = searchResults.value.items.slice(0, itemsToShow)
    itemsForPreview.value = normalizeItems(items)
  } else {
    // Keep existing behavior for selected vehicles
    itemsForPreview.value = selectedPlacement.value.activeVehicles
  }
}

watch(
  () => props.placementId,
  newId => {
    if (newId) {
      setSelectedPlacement(newId)
    }
  },
  { immediate: true },
)

watch(
  () => selectedPlacement.value?.numberOfVehicles,
  () => {
    updatePreviewItems()
  },
  { immediate: false },
)
</script>

<style scoped>
/* Sleek and professional tabs container styling */
.placement-settings-card {
  background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.04),
    0 1px 3px rgba(0, 0, 0, 0.06);
  border-radius: 12px;
}

/* Main tabs styling for consistency and polish */
.main-tabs {
  background: transparent;
}

.main-tabs :deep(.v-tab) {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
  border-radius: 8px 8px 0 0;
  margin-right: 2px;
  transition: all 0.2s ease;
}

.main-tabs :deep(.v-tab:hover) {
  background: rgba(25, 118, 210, 0.04);
  transform: translateY(-1px);
}

.main-tabs :deep(.v-tab--selected) {
  font-weight: 600;
  background: rgba(25, 118, 210, 0.08);
  position: relative;
}

.main-tabs :deep(.v-tab--selected::after) {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: rgb(25, 118, 210);
  border-radius: 1px 1px 0 0;
  box-shadow: 0 1px 3px rgba(25, 118, 210, 0.2);
}

.main-tabs :deep(.v-tab__content) {
  padding: 16px 24px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-tabs :deep(.v-tab__icon) {
  margin-right: 8px;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  font-size: 20px;
}

.main-tabs :deep(.v-tab--selected .v-tab__icon) {
  opacity: 1;
}

/* Tab content consistency */
.v-window-item .v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}

.v-window-item .v-card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: rgb(33, 33, 33);
  padding: 20px 24px 16px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.v-window-item .v-card-text {
  padding: 20px 24px 24px 24px;
}

.v-window-item .v-card-text p {
  margin-bottom: 16px;
}

#micro-srp-admin :deep(.v-label.v-field-label) {
  margin-left: 12px !important;
}

.filters__manual :deep(.v-input__details) {
  display: none;
}

.placement__name :deep(input) {
  font-size: 24px;
  font-weight: bold;
  padding-left: 0;
}

.v-select .v-field .v-field__input > input {
  background: none;
  border: none;
  box-shadow: none;
}

:deep(.v-field__input) {
  background: none;
  border: none;
}

:deep(.v-field--variant-outlined .v-field__input) {
  padding: 12px 16px;
}

:deep(.v-field--variant-filled .v-field__input) {
  box-shadow: none;
  line-height: 1.625em;
  padding: 24px 16px 4px 16px;
}

:deep(
    .v-field.v-field--variant-outlined .v-field__field input[type='text']:focus
  ) {
  box-shadow: none !important;
}

:deep(
    .v-field.v-field--variant-outlined
      .v-field__field
      input[type='number']:focus
  ) {
  box-shadow: none !important;
}

@media (max-width: 425px) {
  :deep(.v-expansion-panel-text__wrapper) {
    padding-left: 10px;
    padding-right: 10px;
  }

  :deep(.v-card-text) {
    padding-left: 5px;
    padding-right: 5px;
  }

  :deep(.v-field__input) {
    padding: 10px !important;
  }

  :deep(
      .v-expansion-panel-text.filters-section
        .v-expansion-panel-text__wrapper
        > div
    ) {
    margin: 0;
  }
}
</style>
